import { Card as HeroUiCard, CardBody, CardHeader, Divider, extendVariants } from '@heroui/react';
import { twMerge } from 'tailwind-merge';

type Props = {
    children: React.ReactNode;
    className?: string;
    header?: React.ReactNode;
    dividers?: { afterHeader?: boolean };
    width?: 342 | 500 | 'full';
    padding?: 0 | 2.5 | 'default' | 'top-6';
    allowOverflow?: boolean;
    radius?: 8 | 2;
    shadow?: boolean;
    startBorderColor?: 'primary' | 'purple';
};

const ExtendedCard = extendVariants(HeroUiCard, {
    variants: {
        shadow: {
            card: {
                base: 'shadow-[0_2px_10px_0_rgba(0,0,0,0.1)]',
            },
            none: {
                base: 'shadow-none',
            },
        },
        // See https://github.com/heroui-inc/heroui/issues/5293 and comment in ImportantCtaBox.tsx
        // for why our radius variants are not yet being applied correctly.
        radius: {
            8: {
                base: 'rounded-8',
                header: 'rounded-t-8',
                footer: 'rounded-b-8',
            },
            2: {
                base: 'rounded-2',
                header: 'rounded-t-2',
                footer: 'rounded-b-2',
            },
        },
    },
    defaultVariants: {
        radius: 8,
        shadow: 'card',
    },
});

function getWidthClass(width: Props['width']) {
    if (width === 342) return 'w-[342px] max-w-full';
    if (width === 500) return 'w-[500px] max-w-full';
    if (width === 'full') return 'w-full';
    return '';
}

type PaddingMap = {
    t: string;
    r: string;
    b: string;
    l: string;
};

const PADDING: Record<NonNullable<Props['padding']>, PaddingMap> = {
    2.5: { t: 'pt-2.5', r: 'pe-2.5', b: 'pb-2.5', l: 'ps-2.5' },
    default: { t: 'pt-4.5', r: 'pe-5', b: 'pb-5', l: 'ps-5' },
    0: { t: 'pt-0', r: 'pe-0', b: 'pb-0', l: 'ps-0' },
    'top-6': { t: 'pt-6', r: 'pe-5', b: 'pb-5', l: 'ps-5' },
};

function getBodyPadding({ header, padding }: Pick<Props, 'padding' | 'header'>): string {
    padding = padding ?? 'default';

    // When there is a header, the top padding is on the header. When there
    // is no header, the top padding is on the body. We can't put padding on the
    // base because we want dividers to stretch to the borders
    const topPadding = header ? 'pt-0' : PADDING[padding].t;

    return `${topPadding} ${PADDING[padding].r} ${PADDING[padding].b} ${PADDING[padding].l}`;
}

function getHeaderPadding({ dividers, padding }: Pick<Props, 'dividers' | 'padding'>): string {
    padding = padding ?? 'default';
    // When there is a divider below the header, we shrink the padding a little
    const paddingBelowHeader = dividers?.afterHeader ? 'pb-2' : 'pb-2.5';

    // See comment in getBodyPadding about the padding on the header
    return `${PADDING[padding].t} ${PADDING[padding].r} ${paddingBelowHeader} ${PADDING[padding].l}`;
}

function getStartBorder(startBorderColor: Props['startBorderColor']) {
    if (!startBorderColor) return '';

    const startBorderColorClassname = {
        primary: 'border-s-primary',
        purple: 'border-s-purple',
    }[startBorderColor];

    return `border-s-4 ${startBorderColorClassname}`;
}

export function Card({
    children,
    className,
    header,
    dividers = { afterHeader: false },
    width,
    padding = 'default',
    allowOverflow = false,
    shadow = true,
    startBorderColor,
    radius = 8,
}: Props) {
    return (
        <ExtendedCard
            classNames={{
                base: twMerge(
                    'bg-white border border-solid border-slate-grey-200 pt-0 pr-0 pb-0 pl-0',
                    getWidthClass(width),
                    allowOverflow && 'overflow-visible',
                    className,
                ),
                header: getHeaderPadding({ dividers, padding }),
                body: twMerge(
                    getBodyPadding({ header, padding }),
                    getStartBorder(startBorderColor),
                    allowOverflow && 'overflow-visible',
                ),
            }}
            shadow={shadow ? 'card' : 'none'}
            radius={radius}
        >
            {header && <CardHeader>{header}</CardHeader>}
            {dividers.afterHeader && header && <Divider className="mb-2" />}
            <CardBody>{children}</CardBody>
        </ExtendedCard>
    );
}
